import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const DepartmentReports = () => {
  const [payrolls, setPayrolls] = useState([]);
  const [departments, setDepartments] = useState([
    "HR",
    "Finance",
    "Engineering",
    "Sales",
    "Support",
  ]); // Could also fetch from API if available
  const [department, setDepartment] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(false);

  const token = "YOUR_BEARER_TOKEN"; // Replace with your auth token

  // Fetch payrolls by department
  const fetchPayrolls = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        "https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls",
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { department, month, year },
        }
      );
      setPayrolls(res.data || []);
    } catch (err) {
      console.error("Error fetching department payrolls:", err);
    } finally {
      setLoading(false);
    }
  };

  // Export via server API
  const exportFromServer = async () => {
    try {
      const res = await axios.get(
        "https://hrmspayroll.prepstripe.com/api/payroll/salary-structures/export",
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { department },
          responseType: "blob", // handle file download
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Department_Report_${department}_${month}_${year}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Error exporting from server:", err);
    }
  };

  // Export to Excel (local)
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(payrolls);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Department Payrolls");
    XLSX.writeFile(wb, `Department_Report_${department}_${month}_${year}.xlsx`);
  };

  // Export to PDF (local)
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text(`Department Report - ${department} (${month}/${year})`, 14, 15);

    doc.autoTable({
      startY: 25,
      head: [
        ["Employee", "Department", "Month", "Year", "Gross Pay", "Net Pay"],
      ],
      body: payrolls.map((row) => [
        row.employee_name,
        row.department,
        row.month,
        row.year,
        row.gross_pay,
        row.net_pay,
      ]),
    });

    doc.save(`Department_Report_${department}_${month}_${year}.pdf`);
  };

  // Fetch on filter change
  useEffect(() => {
    if (department && month && year) {
      fetchPayrolls();
    }
  }, [department, month, year]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold mb-6">🏢 Department Payroll Reports</h1>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <select
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          className="border rounded-md px-3 py-2"
        >
          <option value="">Select Department</option>
          {departments.map((dept) => (
            <option key={dept} value={dept}>
              {dept}
            </option>
          ))}
        </select>

        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="border rounded-md px-3 py-2"
        >
          <option value="">Select Month</option>
          {[...Array(12)].map((_, i) => (
            <option key={i + 1} value={i + 1}>
              {new Date(0, i).toLocaleString("default", { month: "long" })}
            </option>
          ))}
        </select>

        <select
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="border rounded-md px-3 py-2"
        >
          <option value="">Select Year</option>
          {[2023, 2024, 2025].map((yr) => (
            <option key={yr} value={yr}>
              {yr}
            </option>
          ))}
        </select>

        <button
          onClick={fetchPayrolls}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Apply Filters
        </button>
      </div>

      {/* Export Buttons */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={exportFromServer}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Export (Server)
        </button>
        <button
          onClick={exportToExcel}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Export Excel
        </button>
        <button
          onClick={exportToPDF}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Export PDF
        </button>
      </div>

      {loading && <p className="text-gray-600">Loading...</p>}

      {/* Department Payroll Table */}
      <div>
        <h2 className="text-xl font-semibold mb-3">Department Payrolls</h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-white shadow-md rounded-lg">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2 text-left">Employee</th>
                <th className="border px-4 py-2 text-left">Department</th>
                <th className="border px-4 py-2 text-left">Month</th>
                <th className="border px-4 py-2 text-left">Year</th>
                <th className="border px-4 py-2 text-left">Gross Pay</th>
                <th className="border px-4 py-2 text-left">Net Pay</th>
              </tr>
            </thead>
            <tbody>
              {payrolls.length > 0 ? (
                payrolls.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="border px-4 py-2">{row.employee_name}</td>
                    <td className="border px-4 py-2">{row.department}</td>
                    <td className="border px-4 py-2">{row.month}</td>
                    <td className="border px-4 py-2">{row.year}</td>
                    <td className="border px-4 py-2">{row.gross_pay}</td>
                    <td className="border px-4 py-2">{row.net_pay}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-3">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DepartmentReports;
